import React, { useState, useMemo } from "react";
import { PositionComparisonModal } from "./PositionComparisonModal";
import { useGlobalPositions, useGlobalData, useGlobalOrders } from "../hooks/useGlobalHooks";
import BulkModifyModal from "./BulkModifyModal";
import GroupSelectionModal from "./GroupSelectionModal";
import {
  OptimizedNumberCell,
  OptimizedColorCell,
  OptimizedPNLCell,
  LiveAggregatedPNLCell,
  LivePNLCell,
  LiveLTPCell,
} from "./OptimizedCell";
import { usePositionStore } from "../contexts/LTPStore/usePositionsStore";
import WebSocketManager from "../contexts/websocketManager";
import { useWebSocketStatusStore } from "../contexts/LTPStore/useWebSocketStatusStore";
import apiClient from "../utils/apiClient";
import CancelConfirmationModal from "./CancelConfirmationModal";
import ExitSuccessModal from "./ExitSuccessModal";
import server from "../utils/serverConfig";
import { useClosedPositionStore } from "../contexts/LTPStore/useClosedPositionStore";

/**
 * Get broker badge info based on broker name
 * @param {string} broker - Broker name
 * @returns {Object} Badge info with text and color classes
 */
function getBrokerBadge(broker) {
  // console.log("BROKER-----",broker)
  const brokerLower = broker?.toLowerCase() || "";

  if (brokerLower === "fyers") {
    return { text: "FY", class: "bg-blue-800 text-white" };
  } else if (brokerLower === "dhan_hq") {
    return { text: "DHQ", class: "bg-emerald-950 text-white" };
  } else if (brokerLower.includes("dhan")) {
    return { text: "DH", class: "bg-emerald-800 text-white" };
  } else if (brokerLower === "nubra") {
    return { text: "NU", class: "bg-purple-800 text-white" };
  } else if (brokerLower === "zerodha") {
    return { text: "ZE", class: "bg-[#EF5350] text-white" };
  }
  return { text: "??", class: "bg-gray-700 text-white" };
}

/**
 * Get styling for client label badge
 * @param {string} label - Client label
 * @returns {string} Tailwind classes
 */
function getLabelStyles(label) {
  const l = label?.toLowerCase() || "";
  if (l.includes("clearmind")) {
    return "bg-purple-500/20 text-purple-400 border-purple-500/30";
  } else if (l.includes("og")) {
    return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
  } else if (l.includes("satyam")) {
    return "bg-amber-500/20 text-amber-400 border-amber-500/30";
  }
  return "bg-blue-500/20 text-blue-400 border-blue-500/30";
}

/**
 * Get Tailwind color classes based on validation color
 * @param {string} color - 'green' or 'red'
 * @returns {string} Tailwind classes
 */
function getColorClasses(color) {
  switch (color) {
    case "green":
      return "bg-green-500/20 text-green-400";
    case "red":
      return "bg-red-500/20 text-red-400";
    default:
      return "";
  }
}

/**
 * Format number with commas
 * @param {number} num
 * @returns {string}
 */
function formatNumber(num) {
  if (num === undefined || num === null) return "-";
  return num.toLocaleString("en-IN");
}

/**
 * Format PNL with + or - sign and color (without rupee symbol)
 * @param {number} pnl
 * @returns {JSX.Element}
 */
function formatPNL(pnl) {
  if (pnl === undefined || pnl === null || pnl === 0) {
    return <span className="text-gray-500 text-right">-</span>;
  }

  const isProfit = pnl >= 0;
  const sign = isProfit ? "+" : "";

  return (
    <span
      className={`${isProfit ? "text-green-400" : "text-red-400"
        } font-semibold text-right`}
    >
      {sign}
      {formatNumber(pnl)}
    </span>
  );
}

/**
 * PositionView Component
 * Displays client-wise positions grouped by index with color coding
 *
 * @param {Object} props
 * @param {Array} props.data - Processed position data from usePositionsView hook
 * @param {Object} props.baseQtyMap - Base quantity map for each index
 * @param {Array} props.activeIndexes - List of active indexes to display
 * @param {Object} props.symbolStrategyBaseQty - Symbol+Strategy base qty map
 * @param {boolean} props.loading - Initial loading state (only true on first load)
 */
export function PositionView({
  data,
  baseQtyMap,
  activeIndexes,
  symbolStrategyBaseQty,
  loading,
  clientData,
  orders
}) {

  //  console.log("HERE DATA OF POSITIONS ------------------",data);

  const positions = usePositionStore((state) => state.positions);
  const wsStatus = useWebSocketStatusStore((state) => state.status);

  // console.log("❤️ heartbeat inside PositionView:", {
  //   positions
  // });

  const { indexMultipliers, users } = useGlobalData();
  const { refetch: refetchOrders } = useGlobalOrders();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // Bulk Modify state
  const [bulkModifyOpen, setBulkModifyOpen] = useState(false);
  const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
  const [ordersToModify, setOrdersToModify] = useState([]);

  const handleOpenBulkModify = (ordersToMod) => {
    setOrdersToModify(ordersToMod);
    setBulkModifyOpen(true);
  };

  // Table filtering and sort state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleOpenModal = (clientData) => {
    console.log("🎯 Opening modal for client:", clientData);
    setSelectedClient(clientData);
    setModalOpen(true);
  };




  const clientBrokerMap = useMemo(() => {
    const map = {};
    if (clientData && Array.isArray(clientData)) {
      clientData.forEach(item => {
        map[item.clientId] = item.broker;
      });
    }
    return map;
  }, [clientData]);


  // --- Compute Columns to Render (Active Indexes + Orphan Indexes) ---
  const columnsToRender = useMemo(() => {
    const defaultIndexes = activeIndexes || [];
    const orphanIndexes = new Set();


    // console.log("📊 Calculating Columns to Render. Orders:", orders?.length);

    if (orders) {
      orders.forEach(o => {
        const oStatus = (o.status || "").toLowerCase();
        const oSide = String(o.side).toLowerCase();
        const oStatusNo = o.status_no !== undefined ? parseInt(o.status_no, 10) : undefined;

        // Pending Sell Check (Robust)

        const isPending = ((oStatus === "pending") || (oStatus === "Pending")) && oStatusNo === 6; // Fallback if status_no missing
        const isSell = oSide === "-1" || oSide === "2" || oSide === "sell";

        if (isPending && isSell) {
          // Extract index from symbol
          const sym = (o.symbol || "").toUpperCase();

          if (sym.includes("MCX") || sym.includes("NFO") || sym.includes("BFO") || sym.endsWith("FUT")) return;

          // Only consider orders that match the strict index prefixes
          if (sym.includes("BSE:SENSEX")) {
            orphanIndexes.add("sensex");
          } else if (sym.includes("NSE:BANKNIFTY")) {
            orphanIndexes.add("banknifty");
          } else if (sym.includes("NSE:MIDCPNIFTY")) {
            orphanIndexes.add("midcpnifty");
          } else if (sym.includes("NSE:FINNIFTY")) {
            orphanIndexes.add("finnifty");
          } else if (sym.includes("NSE:NIFTY") && !sym.includes("BANKNIFTY") && !sym.includes("FINNIFTY") && !sym.includes("MIDCPNIFTY")) {
            orphanIndexes.add("nifty");
          }
        }
      });
    }

    // Merge: Active + Orphans, deduplicate, case-normalize
    const merged = new Set(defaultIndexes.map(i => i.toLowerCase()));
    orphanIndexes.forEach(i => merged.add(i));

    const result = Array.from(merged).sort((a, b) => {
      const aUpper = a.toUpperCase();
      const bUpper = b.toUpperCase();
      if (aUpper === "NIFTY") return -1;
      if (bUpper === "NIFTY") return 1;
      return a.localeCompare(b);
    });


    return result;
  }, [activeIndexes, orders]);

  const [searchQuery, setSearchQuery] = useState("");

  // --- Cancel Modal State (Lifted from Comparison Modal) ---
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successModalActionType, setSuccessModalActionType] = useState("Exit");
  const [ordersToCancel, setOrdersToCancel] = useState([]);
  const [submittedOrders, setSubmittedOrders] = useState([]);

  // Open Confirmation Modal
  const openCancelModal = (orders) => {
    setOrdersToCancel(orders);
    setConfirmModalOpen(true);
  };

  const handleConfirmCancel = async () => {
    // Note: We do NOT close the modal immediately. 
    // The modal itself handles the loading state via the promise we return.

    if (!ordersToCancel.length) {
      setConfirmModalOpen(false);
      return;
    }

    try {
      setSuccessModalActionType("Cancel");
      // Group orders by "is XTS/Dhan" vs "Others"
      const xtsOrders = [];
      const xtsAnuragOrders = [];
      const godseyeOrders = [];

      ordersToCancel.forEach(order => {
        const clientId = order.clientId || order.client_id;

        // Determine broker: Prioritize clientBrokerMap to catch specific variants like "dhan_xts_anurag"
        // even if the order object just says "xts"
        let broker = "";
        if (clientBrokerMap && clientId) {
          broker = (clientBrokerMap[clientId] || "").toLowerCase();
        }
        if (!broker) {
          broker = (order.broker || "").toLowerCase();
        }
        if (!broker) broker = "fyers"; // Final default

        const payloadOrder = {
          orderid: String(order.order_id || order.orderId),
          clientid: clientId,
          broker: broker,
        };

        if (broker === "dhan_xts" || broker === "xts" || broker === "dhan_ss" || broker === "dhan") {
          xtsOrders.push(payloadOrder);
        } else if (broker === "dhan_xts_anurag" || broker === "dhan_ss_anurag" || broker === "dhan_anurag") {
          xtsAnuragOrders.push(payloadOrder);
        } else {
          godseyeOrders.push(payloadOrder);
        }
      });

      console.log("🚀 Cancelling split:", { xts: xtsOrders.length, xts_anurag: xtsAnuragOrders.length, godseye: godseyeOrders.length });

      const promises = [];

      // 1. Send XTS orders to SS Dhan Server
      if (xtsOrders.length > 0) {
        promises.push(
          apiClient.post(`${server.ss_dhan.baseUrl}manual-cancel-orders-xts`, xtsOrders)
            .then(res => ({ type: 'xts', data: res.data }))
            .catch(err => ({ type: 'xts', error: err }))
        );
      }

      // 1.1 Send XTS Anurag orders to SS Dhan Anurag Server
      if (xtsAnuragOrders.length > 0) {
        promises.push(
          apiClient.post(`${server.ss_dhan_anurag.baseUrl}manual-cancel-orders-xts`, xtsAnuragOrders)
            .then(res => ({ type: 'xts_anurag', data: res.data }))
            .catch(err => ({ type: 'xts_anurag', error: err }))
        );
      }

      // 2. Send other orders to Godseye Server
      if (godseyeOrders.length > 0) {
        promises.push(
          apiClient.post(`${server.godseye.baseUrl}manual_cancel_orders`, godseyeOrders)
            .then(res => ({ type: 'godseye', data: res.data }))
            .catch(err => ({ type: 'godseye', error: err }))
        );
      }

      const results = await Promise.all(promises);
      let allSubmittedOrders = [];
      let errorMsg = "";

      results.forEach(res => {
        if (res.error) {
          console.error(`❌ Error cancelling ${res.type} orders:`, res.error);
          errorMsg += `${res.type.toUpperCase()} failed: ${res.error.response?.data?.detail || res.error.message}. `;
        } else if (res.data && res.data.orders) {
          // Map response back to format expected by Success Modal
          const responseOrders = res.data.orders.map(o => ({
            ...o,
            orderId: o.orderid,
            symbol: ordersToCancel[0]?.symbol
          }));
          allSubmittedOrders = [...allSubmittedOrders, ...responseOrders];
        }
      });

      if (allSubmittedOrders.length > 0) {
        setSubmittedOrders(allSubmittedOrders);
        setSuccessModalOpen(true);
      }

      if (errorMsg) {
        if (allSubmittedOrders.length === 0) {
          alert(`Failed to cancel orders: ${errorMsg}`);
        } else {
          console.warn("Partial cancellation error:", errorMsg);
        }
      }

      // Close the confirmation modal only after operation completes
      setConfirmModalOpen(false);

    } catch (error) {
      console.error("❌ Unexpected error cancelling orders:", error);
      alert(`Unexpected error: ${error.message}`);
      setConfirmModalOpen(false); // Close on error too
    }
  };

  const closedPositions = useClosedPositionStore((state) => state.positions);

  const handleExportExitedClients = () => {
    try {
      if (!users || !indexMultipliers) {
        alert("Wait for user data to load...");
        return;
      }

      const targetSymbol = "NSE:NIFTY26MAR25500CE";
      const targetStrategy = "PDI_MA_PC5";

      // 1. Get all active Fyers clients with Nifty multiplier > 0
      const activeFyersClients = users.filter(u =>
        u.status === "active" &&
        (u.broker || "").toLowerCase() === "fyers"
      );

      const clientsToExport = [];

      activeFyersClients.forEach(user => {
        const clientId = user.clientId || user.client_id;
        const multiplier = getMultiplierForClient(clientId, "NIFTY");

        if (!multiplier || multiplier <= 0) return;

        const expectedQty = multiplier * 65;

        // Find actual current net qty for this symbol+strategy
        let actualQty = 0;
        if (positions && Array.isArray(positions)) {
          const clientPositions = positions.filter(p =>
            (p.client_id || p.clientId) === clientId &&
            p.symbol === targetSymbol &&
            p.strategy === targetStrategy
          );
          clientPositions.forEach(p => {
            actualQty += Math.abs(Number(p.net_qty || p.qty || 0));
          });
        }

        // Check if user is in "Closed Position List" for yesterday/previously
        const isClosed = (closedPositions || []).some(p =>
          (p.client_id || p.clientId) === clientId &&
          p.symbol === targetSymbol
        );

        // We export if actual < expected (missing or partial)
        if (actualQty < expectedQty || isClosed) {
          const missingQty = expectedQty - actualQty;
          if (missingQty > 0) {
            clientsToExport.push({
              client_id: clientId,
              symbol: targetSymbol,
              strategy: targetStrategy,
              qty: missingQty,
              side: 1, // Buy (to re-enter)
              orderType: "MARKET",
              limitPrice: 0,
              stopPrice: 0,
              broker: "fyers"
            });
          }
        }
      });

      if (clientsToExport.length === 0) {
        alert("No exited clients found for NIFTY26MAR25500CE PDI_MA_PC5");
        return;
      }

      // Generate CSV
      const headers = ["client_id", "symbol", "strategy", "qty", "side", "orderType", "limitPrice", "stopPrice", "broker"];
      const csvRows = [headers.join(",")];

      clientsToExport.forEach(row => {
        csvRows.push([
          row.client_id,
          row.symbol,
          row.strategy,
          row.qty,
          row.side,
          row.orderType,
          row.limitPrice,
          row.stopPrice,
          row.broker
        ].join(","));
      });

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `Exited_Clients_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(`✅ Exported ${clientsToExport.length} clients to CSV`);
    } catch (error) {
      console.error("Error exporting exited clients:", error);
      alert("Error generating CSV: " + error.message);
    }
  };



  // === Helper: extract strike from symbol ===
  function extractStrike(symbol) {
    if (!symbol) return "";
    const m = symbol.match(/(\d{3,6})(CE|PE)$/);
    return m ? m[1] : "";
  }

  // === Helper: multiplier lookup for a given clientId + index ===
  // === Helper: multiplier lookup for a given clientId + index ===
  function getMultiplierForClient(clientId, index) {
    if (!indexMultipliers || !clientId || !index) return NaN;

    // Filter all matches
    const matches = indexMultipliers.filter(
      (m) =>
        m.client_id === clientId &&
        String(m.index).toLowerCase() === String(index).toLowerCase()
    );

    // Prioritize 'dhan_xts' or 'dhan_xts_anurag'
    const multObj =
      matches.find((m) => ["dhan_xts", "dhan_xts_anurag"].includes((m.broker || "").toLowerCase())) ||
      matches[0];

    return multObj ? Number(multObj.multiplier) : NaN;
  }

  // === Build mismatchesByClient map ===
  // shape: { [clientId]: [ { symbol, strategy, strike, multiplier, expectedQty, actualQty, reasons: [..], matchingOrders: [...] } ] }
  const mismatchesByClient = useMemo(() => {
    if (!positions || positions.length === 0 || !symbolStrategyBaseQty) return {};

    const map = {};
    const positionsByClient = positions.reduce((acc, p) => {
      const clientId = p.client_id || "UNKNOWN";
      if (!acc[clientId]) acc[clientId] = [];
      acc[clientId].push(p);
      return acc;
    }, {});

    // Track all matched order IDs to find orphans later
    const matchedOrderIds = new Set();
    // const visibleClientIds = new Set(Object.keys(positionsByClient)); // REMOVED RESTRICTION

    Object.entries(positionsByClient).forEach(([clientId, clientPositions]) => {
      const processedKeys = new Set();

      clientPositions.forEach((pos) => {
        const key = `${pos.symbol}_${pos.strategy || "NO_STRATEGY"}`;
        if (processedKeys.has(key)) return;
        processedKeys.add(key);

        // ONLY proceed if we have baseQtyInfo (same as the modal)
        const baseQtyInfo = symbolStrategyBaseQty[key];
        if (!baseQtyInfo) {
          // Skip — we deliberately don't report "missing base" here to match modal
          return;
        }

        const index = baseQtyInfo.index?.toUpperCase();
        const multiplier = getMultiplierForClient(clientId, index);
        const baseQty = Number(baseQtyInfo.baseQty || 0);
        const expectedQty = Number(multiplier) * baseQty;

        let actualQty = 0;
        clientPositions.forEach((p) => {
          const pKey = `${p.symbol}_${p.strategy || "NO_STRATEGY"}`;
          if (pKey === key) actualQty += Math.abs(Number(p.net_qty || p.qty || 0));
        });

        const reasons = [];
        if (!pos.strategy) reasons.push("strategy missing / N/A");
        if (!Number.isFinite(multiplier)) reasons.push("multiplier missing for client/index");
        if (!Number.isFinite(expectedQty)) reasons.push("expectedQty could not be calculated");
        if (Number.isFinite(expectedQty) && expectedQty !== Number(actualQty))
          reasons.push(`qty mismatch (expected ${expectedQty}, got ${actualQty})`);
        if (actualQty === 0 && Number.isFinite(expectedQty) && expectedQty > 0)
          reasons.push("actual qty is 0 but expected > 0");

        // --- Logic to find matching Pending Sell Orders (same as comparison modal) ---
        let matchingOrders = [];
        if (orders) {
          matchingOrders = orders.filter(o => {
            const oClientId = o.clientId || o.client_id;
            const oSymbol = o.symbol;
            const oStrategy = o.strategy;
            const oSide = String(o.side).toLowerCase();
            const oStatus = (o.status || "").toLowerCase();
            const oStatusNo = o.status_no !== undefined ? parseInt(o.status_no, 10) : undefined;

            const currentClientId = (oClientId || "").toString().trim();
            const targetClientId = (clientId || "").toString().trim();
            if (currentClientId !== targetClientId) return false;

            const cleanPosSymbol = (pos.symbol || "").replace(/^MCX:/i, "").trim();
            const cleanOrderSymbol = (oSymbol || "").replace(/^MCX:/i, "").trim();

            const currentStrategy = (oStrategy || "").toString().trim().toUpperCase();
            const targetStrategy = (pos.strategy || "").toString().trim().toUpperCase();

            // Strict Strategy Match
            if (cleanPosSymbol !== cleanOrderSymbol) return false;
            if (currentStrategy !== targetStrategy) return false;

            // Side Match (Sell: -1, 2, "sell")
            const isSell = oSide === "-1" || oSide === "2" || oSide === "sell";
            if (!isSell) return false;

            // Status Match (Robust: Pending OR status_no 6)
            const isPending = (oStatus === "pending" && oStatusNo === 6) || oStatus === "pending";
            return isPending;
          });
        }

        // Track matched IDs
        matchingOrders.forEach(o => matchedOrderIds.add(o.orderid || o.order_id));

        // Logic to calculate Excess Sell Orders (Smart Selection)
        const totalPendingSellQty = matchingOrders.reduce((acc, o) => acc + (parseInt(o.qty) || 0), 0);
        const excessQty = totalPendingSellQty - actualQty;

        // Filter orders to only include the excess ones
        let extraOrders = [];
        if (matchingOrders.length > 0 && excessQty > 0) {
          // Sort descending by Order ID (cancel newest first)
          const sortedOrders = [...matchingOrders].sort((a, b) => {
            const idA = (a.orderid || a.order_id || "").toString();
            const idB = (b.orderid || b.order_id || "").toString();
            return idB.localeCompare(idA);
          });

          let pendingToRemove = excessQty;
          for (const order of sortedOrders) {
            const q = parseInt(order.qty || 0);
            if (q <= pendingToRemove) {
              extraOrders.push(order);
              pendingToRemove -= q;
            }
            if (pendingToRemove <= 0) break;
          }
        }

        if (reasons.length > 0 || extraOrders.length > 0) {
          if (extraOrders.length > 0) {
            // If we have specific extra orders, note them
            reasons.push(`Found ${extraOrders.length} extra pending sell order(s) (Total excess: ${excessQty})`);
          }

          if (!map[clientId]) map[clientId] = [];
          map[clientId].push({
            symbol: pos.symbol,
            strategy: pos.strategy || "N/A",
            strike: extractStrike(pos.symbol),
            multiplier: Number.isFinite(multiplier) ? multiplier : null,
            expectedQty: Number.isFinite(expectedQty) ? expectedQty : null,
            actualQty,
            reasons,
            matchingOrders, // Keep all matches for reference
            extraOrders,    // The specific subset to cancel
            rowType: "position",
          });
        }
      });
    });

    // FIND ORPHAN ORDERS: Pending Sell Orders not matched to any position
    if (orders) {
      orders.forEach(o => {
        const oClientId = (o.clientId || o.client_id || "").toString().trim();
        const oSide = String(o.side).toLowerCase();
        const oStatus = (o.status || "").toLowerCase();
        const oStatusNo = o.status_no !== undefined ? parseInt(o.status_no, 10) : undefined;

        // Only consider orders that match the strict index prefixes
        const sym = (o.symbol || "").toUpperCase();
        let isMatchedIndex = false;

        if (sym.includes("MCX") || sym.includes("NFO") || sym.includes("BFO") || sym.endsWith("FUT")) {
          isMatchedIndex = false;
        } else if (sym.includes("BSE:SENSEX") || sym.includes("NSE:BANKNIFTY") || sym.includes("NSE:MIDCPNIFTY") || sym.includes("NSE:FINNIFTY") || (sym.includes("NSE:NIFTY") && !sym.includes("BANKNIFTY") && !sym.includes("FINNIFTY") && !sym.includes("MIDCPNIFTY"))) {
          isMatchedIndex = true;
        }


        // Must belong to a visible client? NO, we want to capture ALL orphans now
        // if (!visibleClientIds.has(oClientId)) return;

        // Must be Pending Sell
        const isSell = oSide === "-1" || oSide === "2" || oSide === "sell";
        const isPending = oStatus === "pending" && oStatusNo === 6;

        // Must NOT be already matched matchedOrderIds
        const orderId = o.orderid || o.order_id;
        if (isMatchedIndex && isSell && isPending && !matchedOrderIds.has(orderId)) {
          if (!map[oClientId]) map[oClientId] = [];

          // Add orphan entry
          map[oClientId].push({
            symbol: o.symbol,
            strategy: o.strategy || "Unknown",
            strike: extractStrike(o.symbol),
            actualQty: 0,
            expectedQty: 0,
            reasons: ["Orphaned Order (No matching position found)"],
            extraOrders: [o],
            rowType: "orphan"
          });
        }
      });
    }

    return map;
  }, [positions, symbolStrategyBaseQty, indexMultipliers, orders]);

  // Calculate ALL mismatch orders for global "Cancel Extra" button
  const allMismatchOrders = useMemo(() => {
    return Object.values(mismatchesByClient).flatMap(clientIssues =>
      clientIssues.flatMap(issue => issue.extraOrders || [])
    );
  }, [mismatchesByClient]);

  // Handle sort click
  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <svg
          className="w-3 h-3 text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }

    if (sortConfig.direction === "asc") {
      return (
        <svg
          className="w-3 h-3 text-blue-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 15l7-7 7 7"
          />
        </svg>
      );
    }

    return (
      <svg
        className="w-3 h-3 text-blue-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    );
  };

  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    let processed = data.map(row => {
      const user = users?.find(u =>
        (u.clientId || u.client_id || "").toString().trim() === row.clientId.toString().trim()
      );
      return {
        ...row,
        label: user?.label || ""
      };
    });

    // --- INJECT GHOST ROWS FOR ORPHAN-ONLY CLIENTS ---
    // 1. Identify clients with orphans (from mismatchesByClient)
    const clientsWithOrphans = Object.keys(mismatchesByClient);

    // 2. Identify clients already visible in the table
    const visibleClients = new Set(processed.map(r => r.clientId));

    // 3. Find missing clients
    const missingClients = clientsWithOrphans.filter(cId => !visibleClients.has(cId));

    // 4. Create ghost rows for them
    if (missingClients.length > 0) {
      missingClients.forEach(clientId => {
        // Try to find user details if available
        let clientName = clientId;
        let broker = "Unknown";
        let label = "";

        if (users) {
          const userDefaults = users.find(u =>
            (u.clientId || u.client_id || "").toString().trim() === clientId.toString().trim()
          );
          if (userDefaults) {
            clientName = userDefaults.name || userDefaults.clientName || clientId;
            broker = userDefaults.broker || "Unknown";
            label = userDefaults.label || "";
          }
        }

        // Create a "ghost" row
        const ghostRow = {
          clientId: clientId,
          clientName: clientName,
          broker: broker,
          label: label,
          isGhost: true,
          rowType: "orphan"
        };

        // Calculate SL Qty (Orphan Qty) for each active index
        const clientOrphans = mismatchesByClient[clientId] || [];


        // Calculate SL Qty (Orphan Qty) for each column to render
        if (columnsToRender && columnsToRender.length > 0) {
          columnsToRender.forEach(index => {
            // Filter orphans for this index
            const indexOrphans = clientOrphans.filter(item => {
              const sym = (item.symbol || "").toUpperCase();
              // Robust check: match strictly by specified index formats
              const idxUpper = index.toUpperCase();
              if (idxUpper === "SENSEX" && sym.includes("BSE:SENSEX")) return true;
              if (idxUpper === "BANKNIFTY" && sym.includes("NSE:BANKNIFTY")) return true;
              if (idxUpper === "MIDCPNIFTY" && sym.includes("NSE:MIDCPNIFTY")) return true;
              if (idxUpper === "FINNIFTY" && sym.includes("NSE:FINNIFTY")) return true;
              if (idxUpper === "NIFTY" && sym.includes("NSE:NIFTY") && !sym.includes("BANKNIFTY") && !sym.includes("FINNIFTY") && !sym.includes("MIDCPNIFTY")) return true;
              return false;
            });

            // Sum up the quantities
            let totalOrphanQty = 0;
            indexOrphans.forEach(item => {
              if (item.extraOrders) {
                item.extraOrders.forEach(o => {
                  totalOrphanQty += parseInt(o.qty || 0);
                });
              }
            });


            // If we have orphans for this index, populate the cell data
            // We set slQty to totalOrphanQty and others to defaults to show "-" or 0 as appropriate
            if (totalOrphanQty > 0) {
              ghostRow[index] = {
                multiplier: null, // Will show "-"
                qty: 0,           // No active position
                slQty: totalOrphanQty,
                expectedQty: 0,
                color: "red",     // Highlight as issue
                pnl: 0
              };
            }
          });
        }

        processed.push(ghostRow);
      });
    }


    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();

      processed = processed.filter((row) => {
        const broker = row.broker?.toLowerCase() || "";
        const clientId = row.clientId?.toLowerCase() || "";
        const clientName = row.clientName?.toLowerCase() || "";
        const label = row.label?.toLowerCase() || "";

        return (
          broker.includes(q) ||
          clientId.includes(q) ||
          clientName.includes(q) ||
          label.includes(q)
        );
      });
    }

    // =========================================================
    // 1️⃣ APPLY SORTING (supports nested keys with "_")
    // =========================================================
    if (sortConfig.key) {
      processed.sort((a, b) => {
        let aValue, bValue;

        // ---- Nested key handling: "NIFTY_multiplier", "BANKNIFTY_qty" etc. ----
        if (sortConfig.key.includes("_")) {
          const [indexName, property] = sortConfig.key.split("_");

          const map = {
            multiplier: "multiplier",
            qty: "qty",
            slQty: "slQty",
            pnl: "pnl",
          };

          aValue = a[indexName]?.[property] ?? a[indexName]?.[map[property]] ?? 0;
          bValue = b[indexName]?.[property] ?? b[indexName]?.[map[property]] ?? 0;
        }
        // ---- Normal field sorting ----
        else {
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        }

        // null / undefined safe handling
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        // numeric compare
        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
        }

        // string compare
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();

        if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    // =========================================================
    // 2️⃣ PRIORITY SORT: RED VALIDATION (activeIndexes)
    // =========================================================
    processed.sort((a, b) => {
      const aHasRed =
        activeIndexes?.some((index) => a[index]?.color === "red") || false;
      const bHasRed =
        activeIndexes?.some((index) => b[index]?.color === "red") || false;

      if (aHasRed && !bHasRed) return -1;
      if (!aHasRed && bHasRed) return 1;
      return 0;
    });

    // =========================================================
    // 3️⃣ PRIORITY SORT: MISMATCH CLIENTS (mismatchesByClient)
    // =========================================================
    processed.sort((a, b) => {
      const aMismatch = mismatchesByClient?.[a.clientId]?.length > 0;
      const bMismatch = mismatchesByClient?.[b.clientId]?.length > 0;

      if (aMismatch && !bMismatch) return -1;
      if (!aMismatch && bMismatch) return 1;
      return 0;
    });

    return processed;
  }, [data, sortConfig, activeIndexes, mismatchesByClient, searchQuery, users, columnsToRender]);



  // console.log("PROCESSED DATA------",processedData);
  // Show loading state (only on initial load)
  if (loading) {
    return (
      <div className="bg-[#1a1d29] p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <svg
            className="inline-block animate-spin h-12 w-12 text-blue-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="text-gray-400 mt-4">Loading positions...</p>
        </div>
      </div>
    );
  }

  // Show empty state
  if (!data || data.length === 0) {
    return (
      <div className="bg-[#1a1d29] p-6">
        <p className="text-gray-400">No position data available</p>
      </div>
    );
  }


  // console.log("NORMAL POSITION VIEW DATA----------",positions);

  return (
    <>
      {/* Custom Scrollbar Styles */}
      <style>{`
        .sleek-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .sleek-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .sleek-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(59, 130, 246, 0.3);
          border-radius: 10px;
        }
        .sleek-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(59, 130, 246, 0.5);
        }
      `}</style>
      <div className="flex flex-col h-full">
        {/* Base Qty Info and Legend in same row */}
        <div className="flex justify-between items-center p-3 mb-3 bg-[#14161f] flex-wrap gap-3">
          {/* Base Qty Info */}
          {baseQtyMap && Object.keys(baseQtyMap).length > 0 && (
            <div className="flex flex-wrap gap-3 items-center">
              <span className="text-gray-400 font-medium text-sm">
                Base Qty (1x):
              </span>
              {Object.entries(baseQtyMap).map(([index, qty]) => (
                <span
                  key={index}
                  className="bg-[#1a1d29] px-2 py-1 rounded text-sm text-gray-300"
                >
                  <span className="font-semibold text-white">
                    {index.toUpperCase()}:
                  </span>{" "}
                  {formatNumber(qty)}
                </span>
              ))}
            </div>
          )}
          {/* Search Input */}



          {/* Legend */}
          <div className="flex flex-wrap gap-4 text-xs items-center">


            {/* Global Cancel Button for Mismatches */}
            {allMismatchOrders.length > 0 && (
              <button
                onClick={() => openCancelModal(allMismatchOrders)}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-1.5 px-3 rounded shadow-lg shadow-red-900/30 flex items-center gap-2 animate-pulse"
              >
                <span className="text-sm">⚠️</span>
                Cancel Extra Sell Orders ({allMismatchOrders.length})
              </button>
            )}

            {/* Bulk Modify Button */}
            <button
              onClick={() => setIsSelectionModalOpen(true)}
              className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-black uppercase tracking-wider rounded shadow-lg shadow-indigo-900/40 transition-all flex items-center gap-2 border border-indigo-400/30"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span>Bulk SL Order Modify</span>
            </button>

            {/* <button
              onClick={handleExportExitedClients}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-1.5 px-3 rounded shadow-lg shadow-blue-900/30 flex items-center gap-2"
              title="Export clients who exited NIFTY26MAR25500CE PDI_MA_PC5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export Exited Clients
            </button> */}

            <div className="flex items-center gap-2">
              <span className="w-4 h-4 bg-green-500/20 border border-green-500/50 rounded"></span>
              <span className="text-gray-400">All OK (Qty & SL Match)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 bg-red-500/20 border border-red-500/50 rounded"></span>
              <span className="text-gray-400">Issue (Qty or SL Mismatch)</span>
            </div>
            {/* Controls: Bot status + Ping / Refresh / Reconnect */}


          </div>
        </div>

        <div className="relative">
          <input
            type="text"
            placeholder="Search broker / client id / name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="
    bg-[#0f1116]
    border border-gray-700
    text-gray-200
    text-sm
    px-3 py-1.5
    rounded
    w-64
    ml-6
    focus:outline-none
    focus:ring-1
    focus:ring-blue-500
    placeholder-gray-500
  "
          />

        </div>
        {/* Position Table */}
        <div className="flex-1 overflow-auto sleek-scrollbar">

          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-[#14161f] z-10">
              <tr className="shadow-[inset_0_-1px_0_0_#1F2937]
 border-gray-700/50">
                <th
                  rowSpan="2"
                  className="text-center py-2 text-gray-400 font-medium bg-[#14161f] z-20 border-r border-gray-700/50"
                >
                  No.
                </th>
                <th
                  rowSpan="2"
                  className="text-left py-2 px-2 text-gray-300 font-medium bg-[#14161f] z-20 border-r border-gray-700/50 min-w-[220px] w-[220px] cursor-pointer hover:bg-[#1a1d29] transition-colors"
                  onClick={() => handleSort("clientName")}
                >
                  <div className="flex items-center gap-1">
                    <span>Name</span>
                    {getSortIcon("clientName")}
                  </div>



                </th>
                {columnsToRender &&
                  columnsToRender.map((index) => (
                    <th
                      key={index}
                      colSpan="3"
                      className="text-center py-2 px-2 text-white font-semibold border-l border-gray-700/50 cursor-pointer hover:bg-[#1a1d29] transition-colors"
                      onClick={() => handleSort(index)}
                    >
                      <div className="flex items-center justify-center gap-1">
                        <span>{index.toUpperCase()}</span>
                        {getSortIcon(index)}
                      </div>
                    </th>
                  ))}
              </tr>
              <tr className="shadow-[inset_0_-1px_0_0_#1F2937]
 border-gray-700/50">
                {columnsToRender &&
                  columnsToRender.map((index) => (
                    <React.Fragment key={index}>
                      <th
                        className="text-center py-1 px-1 text-gray-400 text-xs font-medium border-l border-gray-700/50 w-12 cursor-pointer hover:bg-[#1a1d29] transition-colors"
                        onClick={() => handleSort(`${index}_multiplier`)}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <span>Mul</span>
                          {getSortIcon(`${index}_multiplier`)}
                        </div>
                      </th>
                      <th
                        className="text-center py-1 px-1 text-gray-400 text-xs font-medium 
 shadow-[inset_1px_0_0_0_rgba(55,65,81,0.3)]
 w-20 cursor-pointer hover:bg-[#1a1d29] transition-colors"
                        onClick={() => handleSort(`${index}_qty`)}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <span>QTY</span>
                          {getSortIcon(`${index}_qty`)}
                        </div>
                      </th>
                      <th
                        className="text-center py-1 px-1 text-gray-400 text-xs font-medium shadow-[inset_1px_0_0_0_rgba(55,65,81,0.3)]
 w-20 cursor-pointer hover:bg-[#1a1d29] transition-colors"
                        onClick={() => handleSort(`${index}_slQty`)}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <span>SL QTY</span>
                          {getSortIcon(`${index}_slQty`)}
                        </div>
                      </th>
                    </React.Fragment>
                  ))}
              </tr>
            </thead>
            <tbody>
              {processedData.map((row, idx) => (
                //   console.log("Row here------------",row),
                <tr
                  key={idx}
                  className="shadow-[inset_0_-1px_0_0_#1F2937]
 border-gray-800/50 hover:bg-[#14161f] transition-colors"
                >
                  <td className="py-1 text-center text-gray-500 bg-[#1a1d29] border-r border-gray-700/50 w-3">
                    {idx + 1}
                  </td>
                  <td className="py-1.5 px-2 text-white font-medium border-r border-gray-700/50">
                    <div className="flex justify-between items-center gap-2">
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-1.5 py-0.5 rounded text-xs font-bold ${getBrokerBadge(row.broker).class
                            }`}
                        >
                          {getBrokerBadge(row.broker).text}
                        </span>
                        <span>
                          {row.clientName}{" "}
                          {row.label && (
                            <span
                              className={`text-[10px] px-1 rounded font-bold border ml-1 ${getLabelStyles(row.label)}`}
                              title={row.label}
                            >
                              {row.label.charAt(0).toUpperCase()}
                            </span>
                          )}
                          <span className="text-xs text-gray-500 ml-1">
                            ({row.clientId})
                          </span>

                        </span>
                        {mismatchesByClient[row.clientId] && mismatchesByClient[row.clientId].length > 0 && (
                          <div className="relative inline-block group">
                            {/* Badge Icon */}
                            <span className="bg-red-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full cursor-pointer select-none">
                              !
                            </span>

                            {/* Tooltip */}
                            <div className="
      absolute left-full top-1/2 -translate-y-1/2 ml-2
      hidden group-hover:block
      bg-gray-900 text-gray-100 text-sm p-3 rounded-lg shadow-xl
      w-80 max-h-96 overflow-y-auto
      border border-gray-700
      z-[9999]
      will-change-transform
      transition-all duration-200
    ">
                              {/* Header */}
                              <div className="font-semibold text-red-400 mb-2 text-sm flex items-center justify-between">
                                <span>Mismatch Details</span>
                                <span className="text-gray-400 text-xs">{mismatchesByClient[row.clientId].length} issue{mismatchesByClient[row.clientId].length > 1 ? 's' : ''}</span>
                              </div>

                              {/* Issues List */}
                              {mismatchesByClient[row.clientId].map((issue, i) => (
                                <div key={i} className="mb-3 p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors">
                                  <div className="text-blue-400 font-medium text-sm mb-1">
                                    {issue.symbol} ({issue.strategy})
                                  </div>
                                  <ul className="list-disc list-inside text-gray-300 text-xs space-y-0.5">
                                    {issue.reasons.map((r, j) => (
                                      <li key={j} className="leading-snug">{r}</li>
                                    ))}
                                  </ul>

                                  {/* Cancel Button if matching orders exist */}
                                  {issue.matchingOrders && issue.matchingOrders.length > 0 && (
                                    <div className="mt-2 pt-2 border-t border-gray-700">
                                      <div className="text-xs text-gray-400 mb-1">
                                        Found {issue.matchingOrders.length} pending sell order(s)
                                      </div>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openCancelModal(issue.matchingOrders);
                                        }}
                                        className="w-full bg-red-900/30 hover:bg-red-600 text-red-300 hover:text-white text-xs py-1 rounded transition-colors border border-red-800/50"
                                      >
                                        Cancel All ({issue.matchingOrders.reduce((acc, o) => acc + (parseInt(o.qty) || 0), 0)} Qty)
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}


                      </div>
                      <button
                        onClick={() => handleOpenModal(row)}
                        className="text-blue-400 hover:text-blue-300 transition-colors shrink-0"
                        title="Compare positions"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                  {/* Index Columns */}
                  {columnsToRender.map((index) => {
                    const indexData = row[index];
                    // Handle ghost row data explicitly if needed, but data object handles SL Qty
                    // If indexData is undefined, we assume it's just missing/empty for this index
                    if (!indexData) {
                      return (
                        <React.Fragment key={index}>
                          <td className="py-1.5 px-1 text-center text-gray-600 border-l border-gray-700/50">
                            -
                          </td>
                          <td className="py-1.5 px-1 text-center text-gray-600 border-l border-gray-700/30">
                            -
                          </td>
                          <td className="py-1.5 px-1 text-center text-gray-600 border-l border-gray-700/30">
                            -
                          </td>
                        </React.Fragment>
                      );
                    }

                    // If data exists (even if partial like for ghost rows), render cells
                    return (
                      <React.Fragment key={index}>
                        <td className="py-1.5 px-1 text-center text-gray-300 border-l border-gray-700/50">
                          <OptimizedNumberCell
                            value={indexData.multiplier}
                            className="text-center text-gray-300"
                          />
                        </td>
                        <td className="py-1.5 px-1 text-center font-medium border-l border-gray-700/30">
                          <OptimizedColorCell
                            value={indexData.qty}
                            color={indexData.color}
                            expectedValue={indexData.expectedQty}
                            formatFn={formatNumber}
                            className="text-center font-medium"
                            applyColorToCell={true}
                          />
                        </td>
                        <td className="py-1.5 px-1 text-center font-medium border-l border-gray-700/30">
                          <OptimizedColorCell
                            value={indexData.slQty}
                            color={
                              (Number(indexData.slQty) || 0) > (Number(indexData.qty) || 0)
                                ? "orange"
                                : indexData.color
                            }
                            formatFn={formatNumber}
                            className="text-center font-medium"
                            applyColorToCell={true}
                          />
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Position Comparison Modal */}
        <PositionComparisonModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          clientData={selectedClient}
          positions={positions}
          symbolStrategyBaseQty={symbolStrategyBaseQty}
          orders={orders}
          onCancel={openCancelModal}
        />

        <CancelConfirmationModal
          isOpen={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          onConfirm={handleConfirmCancel}
          orders={ordersToCancel}
        />

        <ExitSuccessModal
          isOpen={successModalOpen}
          onClose={() => setSuccessModalOpen(false)}
          orders={submittedOrders}
          actionType={successModalActionType}
        />

        <BulkModifyModal
          isOpen={bulkModifyOpen}
          onClose={() => setBulkModifyOpen(false)}
          orders={ordersToModify}
          onSuccess={() => refetchOrders()}
        />

        <GroupSelectionModal
          isOpen={isSelectionModalOpen}
          onClose={() => setIsSelectionModalOpen(false)}
          orders={orders}
          positions={positions}
          onConfirm={handleOpenBulkModify}
          assetFilterType="positionSL"
        />
      </div>
    </>
  );
}
